import React, { useState, useEffect, useMemo } from 'react';
import { Provider, useAtom } from 'jotai';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { darkModeAtom } from './state/theme';
import { Routes, Route, Outlet } from 'react-router-dom';
import InputEditPage from './pages/InputEdit';
import WeeklyReportPage from './pages/WeeklyReport';
import MasterManagePage from './pages/MasterManage';
import ClassificationMarketPage from './pages/ClassificationMarket';
import EnlightSyncCheckPage from './pages/EnlightSyncCheck';
import WeeklyThreadWindow from './pages/WeeklyThreadWindow';
import AccessDenied from './pages/AccessDenied';
import './index.css'; // 全体スタイル
import Header from './components/common/Header/Header';
import ThreadHeader from './components/common/Header/ThreadHeader';
import { WebSocketProvider } from './context/WebSocketContext';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { apiClient } from './lib/apiClient';
import MultiSelectWithCheckboxes, { Suggestion } from './components/common/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';

import { Authenticator } from '@aws-amplify/ui-react';
import {
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import '@aws-amplify/ui-react/styles.css';

import { UserContext, UserType, getCognitoUserGroups } from './context/UserContext';
import { UserNameProvider } from './context/UserNameContext';
import { DbStatusChecker } from './lib/DbStatusChecker';
import { UserInfoModalController } from './components/common/Modal/UserInfoModal';
import InformationNoticeDialogController from './components/common/Modal/InformationNoticeDialog';
import { UserProfileProvider, useUserProfile } from './context/UserProfileContext';
import { ENV } from './constants';
import LoginClosedGate from './components/common/LoginClosedGate/LoginClosedGate';


const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: '#94a3b8' },
    // 未指定だと MUI dark デフォルトのピンク (#ce93d8) になり周囲と調和しないため明示
    secondary:  { main: '#94a3b8' },
    background: { default: '#0b1120', paper: '#161f2e' },
    text:       { primary: '#e2e8f0', secondary: '#94a3b8' },
    divider:    '#334155',
    action:     { hover: 'rgba(255,255,255,0.06)', selected: 'rgba(255,255,255,0.10)' },
  },
  components: {
    // dark の elevation オーバーレイ（面が明るく浮く）を無効化し、指定どおりの暗い紺色を維持する
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    // MUI dark デフォルトの disabled (rgba(255,255,255,0.12)/0.3) は背景に溶けて視認できない
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#0284c7',
          color: '#fff',
          '&:hover': { backgroundColor: '#0ea5e9' },
        },
        containedSecondary: {
          backgroundColor: '#475569',
          color: '#e2e8f0',
          '&:hover': { backgroundColor: '#526074' },
        },
        // マスタ管理の「課を修正する」「設定変更」等。dark では緑が浮くため水色系に統一
        containedSuccess: {
          backgroundColor: '#0284c7',
          color: '#fff',
          '&:hover': { backgroundColor: '#0ea5e9' },
        },
        contained: {
          // disabled でも文字が読めるようコントラストを確保（bg は muted のまま = 無効と分かる）
          '&.Mui-disabled': { backgroundColor: '#3b4a61', color: '#cbd5e1' },
        },
        outlined: {
          color: '#e2e8f0',
          borderColor: '#7c8aa0',
          '&:hover': { borderColor: '#94a3b8', backgroundColor: 'rgba(148,163,184,0.12)' },
          '&.Mui-disabled': { color: '#64748b', borderColor: '#334155' },
        },
        textPrimary: {
          color: '#cbd5e1',
        },
        text: {
          '&.Mui-disabled': { color: '#64748b' },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: '#38bdf8' },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0284c7', opacity: 1 },
        },
        track: { backgroundColor: '#475569', opacity: 1 },
      },
    },
    // チェック状態はアクセント水色で統一（palette.primary はスレートのため）
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#38bdf8' },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: '#38bdf8' },
        },
      },
    },
  },
});

const lightTheme = createTheme({ palette: { mode: 'light' } });

// ダークモード atom を読んで MUI ThemeProvider を提供するラッパー
const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark] = useAtom(darkModeAtom);
  const theme = isDark ? darkTheme : lightTheme;

  // data-theme 属性を <html> に付与して CSS 変数ベースのスタイルも追従させる
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, [isDark]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// レイアウトコンポーネント（ヘッダー付き・スレッド用）
export const DefaultLayout: React.FC<{ signOut: () => void }> = ({ signOut }) => {
  const theme = useTheme();
  return (
    <>
      <Header signOut={signOut} />
      <div className="app-container" style={{ backgroundColor: theme.palette.background.default }}>
        <Outlet />
      </div>
    </>
  );
};

export const ThreadLayout: React.FC = () => {
  const theme = useTheme();
  return (
    <>
      <ThreadHeader />
      <div className="app-container" style={{ backgroundColor: theme.palette.background.default }}>
        <Outlet />
      </div>
    </>
  );
};

// Protected routes component to use useUserProfile hook
const ProtectedRoutes: React.FC<{ signOut: () => void }> = ({ signOut }) => {
  const { profile, loaded } = useUserProfile();
  return (
    <Routes>
      <Route element={<DefaultLayout signOut={signOut} />}>
        <Route path="/" element={<InputEditPage />} />
        <Route path="/weekly-report" element={<WeeklyReportPage />} />
        <Route
          path="/master-manage"
          element={
            !loaded
              ? <div />
              : <MasterManagePage />
          }
        />
        <Route
          path="/classification-market"
          element={
            !loaded
              ? <div />
              : <ClassificationMarketPage />
          }
        />
        <Route
          path="/enlight-sync-check"
          element={
            !loaded
              ? <div />
              : <EnlightSyncCheckPage />
          }
        />
        <Route path="/access-denied" element={<AccessDenied />} />
      </Route>
      <Route element={<ThreadLayout />}>
        <Route path="/weekly-thread" element={<WeeklyThreadWindow />} />
      </Route>
    </Routes>
  );
};

type CognitoUserWithInfo = {
  group: string | null;
  department: string | null;
  last_name: string | null;
};

const EMPTY_SUGGESTIONS: Suggestion[] = [];

const SignInFooter: React.FC = () => {
  const forgotPasswordLink = ENV.FORGOT_PASSWORD_LINK;
  const appManagerMailAddress = ENV.APP_MANAGER_MAILADDRESS;
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const [users, setUsers] = useState<CognitoUserWithInfo[]>([]);
  const [selectedDepts, setSelectedDepts] = useState<Suggestion[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const handleOpenDialog = async () => {
    setSupportDialogOpen(true);
    setDialogKey(k => k + 1);
    setSelectedDepts([]);
    setLoadingUsers(true);
    try {
      const res = await apiClient.get('/get_cognito_users_with_user_info');
      setUsers(res.data ?? []);
    } catch {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const departmentSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const sorted = users
      .filter(u => u.department != null && u.department !== '')
      .map(u => u.department as string)
      .filter(d => { if (seen.has(d)) return false; seen.add(d); return true; })
      .sort((a, b) => a.localeCompare(b, 'ja'));
    return [...sorted.map((d, i) => ({ id: i, label: d }))];
  }, [users]);

  const selectedRealDeptLabels = selectedDepts.map(d => d.label);

  const filteredUsers = useMemo(
    () => users
      .filter(u => u.department != null && selectedRealDeptLabels.includes(u.department))
      .sort((a, b) => {
        const deptCmp = (a.department ?? '').localeCompare(b.department ?? '', 'ja');
        if (deptCmp !== 0) return deptCmp;
        return (a.last_name ?? '').localeCompare(b.last_name ?? '', 'ja');
      }),
    [users, selectedDepts],
  );

  return (
    <div className="weeklyAuxiliaryGroup">
      <div className="weeklyAuxiliaryArea">
        <a
          href={forgotPasswordLink || '#'}
          className="weeklyForgotLink"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!forgotPasswordLink) {
              e.preventDefault();
            }
          }}
        >
          パスワードをお忘れですか？
        </a>
      </div>
      <div className="weeklySupportDivider" aria-hidden="true">
        <span className="weeklySupportDividerLine" />
        <span className="weeklySupportDividerText">または</span>
        <span className="weeklySupportDividerLine" />
      </div>
      <div className="weeklySupportArea">
        <a
          href="#"
          className="weeklySupportLink"
          onClick={(e) => {
            e.preventDefault();
            handleOpenDialog();
          }}
        >
          困ったときは
        </a>
      </div>
      <Dialog
        open={supportDialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick') setSupportDialogOpen(false); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>お問い合わせ</DialogTitle>
        <DialogContent dividers>
          <p className="weeklySupportDialogMessage">
            新規登録、ログインに関するお問い合わせは各課担当者にお問い合わせください。
          </p>
          {loadingUsers ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <CircularProgress size={24} />
            </div>
          ) : (
            <>
              <Box
                sx={{
                  marginTop: 2,
                  marginBottom: 2,
                  '& .MuiInputBase-root': { height: 'auto !important', minHeight: '40px' },
                  '& .MuiOutlinedInput-root': { height: 'auto !important', minHeight: '40px' },
                }}
              >
                <MultiSelectWithCheckboxes
                  key={dialogKey}
                  suggestions={departmentSuggestions}
                  placeholder="部署を選択"
                  initialValue={EMPTY_SUGGESTIONS}
                  onChange={setSelectedDepts}
                />
              </Box>
              {selectedRealDeptLabels.length > 0 && (
                filteredUsers.length === 0 ? (
                  <p style={{ color: '#888', margin: '0 0 12px' }}>担当者が登録されていません</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {filteredUsers.map((u, i) => (
                      <Card key={i} variant="outlined" style={{ minWidth: 80 }}>
                        <CardContent style={{ padding: '8px 16px', paddingBottom: 8 }}>
                          {selectedRealDeptLabels.length > 1 && u.department && (
                            <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: 2 }}>{u.department}</div>
                          )}
                          {u.last_name ?? '（名前未登録）'}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )
              )}
            </>
          )}
          <p className="weeklySupportDialogMessage">
            その他のお問合せについては下記へお願いします。
          </p>
          <p>
            <a
              href={`mailto:${appManagerMailAddress}`}
              className="weeklySupportDialogMailLink"
            >
              {appManagerMailAddress}
            </a>
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// サインイン後のアプリ本体（通常ログイン／Hosted UIログインの両方から共通利用）
const AuthenticatedApp: React.FC<{ user: UserType; signOut: () => void }> = ({ user: authUser, signOut }) => {
  return (
    <Provider>
      <AppThemeProvider>
        <UserContext.Provider value={authUser ?? null}>
          <UserProfileProvider>
            <UserInfoModalController />
            <InformationNoticeDialogController />
            <UserNameProvider>
              <WebSocketProvider>
                <ProtectedRoutes signOut={signOut} />
              </WebSocketProvider>
            </UserNameProvider>
          </UserProfileProvider>
        </UserContext.Provider>
      </AppThemeProvider>
    </Provider>
  );
};

// Hosted UI（外部IDプロバイダ連携）ログイン用の画面
// 通常のID/パスワード入力フォームは表示せず、「サインイン」ボタンのみ表示してCognito Hosted UIへ遷移する
const HostedUiGate: React.FC = () => {
  const [authUser, setAuthUser] = useState<UserType>(null);
  const [checking, setChecking] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  const checkCurrentUser = async () => {
    try {
      const userObj = await getCurrentUser();
      setAuthUser(userObj);
    } catch {
      setAuthUser(null);
    } finally {
      setChecking(false);
      setRedirecting(false);
    }
  };

  useEffect(() => {
    checkCurrentUser();
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
        case 'signedIn':
          checkCurrentUser();
          break;
        case 'signInWithRedirect_failure':
          setChecking(false);
          setRedirecting(false);
          break;
        case 'signedOut':
          setAuthUser(null);
          break;
      }
    });
    return unsubscribe;
  }, []);

  const handleSignIn = () => {
    setRedirecting(true);
    signInWithRedirect(
      ENV.COGNITO_OAUTH_PROVIDER
        ? { provider: { custom: ENV.COGNITO_OAUTH_PROVIDER } }
        : undefined
    );
  };

  const handleSignOut = async () => {
    await amplifySignOut();
    setAuthUser(null);
  };

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authUser) {
    return (
      <div className="weeklySignInPage">
        <div className="weeklySignInHeader">
          <div className="weeklySignInIconWrap" aria-hidden="true">
            <DescriptionOutlinedIcon className="weeklySignInIcon" />
          </div>
          <p className="weeklySignInBrand">Weekly Report App</p>
          <h1 className="weeklySignInTitle">週報APPにサインイン</h1>
          <span className="weeklySignInTitleBar" aria-hidden="true" />
        </div>
        <Button variant="contained" onClick={handleSignIn} disabled={redirecting}>
          サインイン
        </Button>
      </div>
    );
  }

  return <AuthenticatedApp user={authUser} signOut={handleSignOut} />;
};

const MainApp: React.FC = () => {
  // userはobject型で管理
  const [user, setUser] = useState<UserType>(null);
  const [attr, setAttr] = useState<string>('');
  const [session, setSession] = useState<string>('');
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false);
  const [dbUnavailable, setDbUnavailable] = useState<boolean>(false);
  const [dbRetrying, setDbRetrying] = useState<boolean>(false);
  const [groups, setGroups] = useState<string[]>([]);

  //セッション情報取得（ログイン成功後、Cognitoグループも取得）
  const getCurrentUserAsync = async () => {
    try {
      let userObj = await getCurrentUser();
      let attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const groupsArr = await getCognitoUserGroups();

      setUser(userObj);
      setAttr(JSON.stringify(attributes, null, 2));
      setSession(JSON.stringify(session, null, 2));
      setIsSignedIn(true);
      setGroups(groupsArr);

      // cross-account かつ Admin API 権限なしのため、トークンの 'cognito:groups' クレームで代替
      console.log('[Cognito] groups via token claims', groupsArr);
    } catch {
      setIsSignedIn(false);
      setGroups([]);
    }
  };

  // Amplify AuthenticatorのuserがセットされたタイミングでgetCurrentUserAsyncを呼ぶ
  useEffect(() => {
    // Amplify UIのAuthenticatorはchildren functionにuser: AuthUser | undefinedを渡す
    // userがundefined→objectになったタイミングで呼ぶ
    // ここではUserContextのuserを監視
    if (user) {
      getCurrentUserAsync();
    }
  }, [user]);

  // DB接続不可時はAmplify Authenticatorのサインインsubmitを無効化（Enterキー送信も抑止）
  useEffect(() => {
    if (!dbUnavailable) return;
    const form = document.querySelector('.db-unavailable form') as HTMLFormElement | null;
    if (!form) return;
    const handler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    form.addEventListener('submit', handler, true);
    return () => {
      form.removeEventListener('submit', handler, true);
    };
  }, [dbUnavailable]);

  return (
    <>
      <DbStatusChecker
        onUnavailable={() => setDbUnavailable(true)}
        onAvailable={() => setDbUnavailable(false)}
        onRetrying={(isRetrying) => setDbRetrying(isRetrying)}
      />
      {ENV.HOSTED_UI_ENABLED ? (
        <HostedUiGate />
      ) : (
        <Authenticator
          hideSignUp={true}
          className={`weeklySignInPage${dbUnavailable ? ' db-unavailable' : ''}`}
          formFields={{
            signIn: {
              username: {
                label: 'Email',
                placeholder: 'Enter your Email',
                isRequired: true,
              },
              password: {
                label: 'Password',
                placeholder: 'Enter your Password',
                isRequired: true,
              },
            },
          }}
          components={{
            SignIn: {
              Header() {
                return (
                  <div className="weeklySignInHeader">
                    <div className="weeklySignInIconWrap" aria-hidden="true">
                      <DescriptionOutlinedIcon className="weeklySignInIcon" />
                    </div>
                    <p className="weeklySignInBrand">Weekly Report App</p>
                    <h1 className="weeklySignInTitle">週報APPにサインイン</h1>
                    <span className="weeklySignInTitleBar" aria-hidden="true" />
                  </div>
                );
              },
              Footer() {
                return <SignInFooter />;
              },
            },
          }}
        >
          {({ signOut, user: authUser }) => {
            // AmplifyのsignOut((data?) => void | undefined) を
            // ヘッダーや各コンポーネントで使いやすいようにラップ
            const handleSignOut = () => {
              if (signOut) {
                signOut();
              }
            };

            return <AuthenticatedApp user={authUser ?? null} signOut={handleSignOut} />;
          }}
        </Authenticator>
      )}
      {dbRetrying && (
        <footer className="error-footer">
          <div className="info-banner" role="status">
            データベースに接続できません。再接続を試行中です…
          </div>
        </footer>
      )}
      {!dbRetrying && dbUnavailable && (
        <footer className="error-footer">
          <div className="error-banner" role="alert">
            データベースに接続できませんでした。管理者にお問い合わせください。
          </div>
        </footer>
      )}
    </>
  );
};

// アプリ環境移行に伴うログイン封鎖モード（REACT_APP_LOGIN_CLOSED）のビルドでは、
// 認証・DB接続確認を一切行わず、新アプリへの案内のみ表示する
const App: React.FC = () => (ENV.LOGIN_CLOSED ? <LoginClosedGate /> : <MainApp />);

export default App;
